import { AudioPresets, Track, VideoPreset } from 'livekit-client';
import type {
  ScalabilityMode,
  TrackPublishOptions,
  VideoCaptureOptions,
  VideoCodec,
} from 'livekit-client';

/**
 * M1 publish quality, revised for the camera-quality slice: picks the camera publish
 * path from the browser's video send capabilities and derives the livekit-client
 * capture/publish options for it, sized by a hardware-aware publish tier.
 *
 * Codec decision (verified against livekit-client 2.22.3 installed types and docs):
 *  - H264 is preferred for the camera. Hardware H264 encoders are essentially always
 *    present on the platforms that matter - VideoToolbox on macOS, Media Foundation /
 *    D3D11 on Windows, VAAPI on Linux desktops - while the previously preferred paths
 *    (VP9 SVC, VP8) run through libvpx SOFTWARE encoders in Chromium: a 720p software
 *    encode competes with the whole call UI for CPU and the picture breaks up into
 *    blocks exactly on the machines with good cameras. The browser side is verified
 *    through `RTCRtpSender.getCapabilities('video')`.
 *  - VP9 SVC stays as the fallback for browsers without H264 (rare): the SDK publishes
 *    VP9/AV1 as SVC (one RTP stream carrying every spatial layer) when
 *    `videoCodec: 'vp9'` is set, honours `scalabilityMode` and force-enables dynacast
 *    for SVC codecs; `backupCodec` (default true) auto-publishes a VP8 track for
 *    subscribers that cannot decode VP9 SVC. SVC is only chosen for VP9 profile 0
 *    (profile-id absent or 0 in sdpFmtpLine) plus a runtime-announced scalability mode
 *    with 3 spatial layers (the non-standard Chromium `scalabilityModes` string).
 *  - Everything else (VP9-only browsers without SVC, capabilities missing entirely,
 *    unknown codec set) publishes VP8 simulcast as the safe default.
 *
 * Layers and bitrates (the camera-quality slice's table, applied per publish tier):
 *  - 180p 320x180  @20  150 kbps
 *  - 360p 640x360  @20  500 kbps
 *  - 720p 1280x720 @30  2.3 Mbps
 *  - 1080p 1920x1080 @30 4.0 Mbps
 * Simulcast publishes the tier's primary with the two layers below it
 * (high: 360p+720p, medium: 180p+360p, low: 180p); SVC publishes one stream at the
 * tier's primary bitrate with L3T3_KEY (three spatial layers the SFU steps through).
 * The SDK's per-source degradationPreference default ('maintain-framerate' for the
 * camera) is kept - conferencing wants smooth motion over per-frame detail.
 */

/** Publish path identifier, also the `publish-mode` testid value. */
export type PublishMode = 'vp9-svc' | 'vp8-simulcast' | 'h264-simulcast';

/** The publish path chosen for the camera, with the reason recorded for debugging. */
export interface PublishDecision {
  readonly mode: PublishMode;
  readonly codec: VideoCodec;
  /** SVC only: the scalabilityMode handed to livekit-client. */
  readonly scalabilityMode?: ScalabilityMode;
  readonly rationale: string;
}

/** SVC mode for the VP9 path: 3 spatial x 3 temporal layers with key-picture signalling. */
export const SVC_SCALABILITY_MODE: ScalabilityMode = 'L3T3_KEY';

/** Any scalability mode with three spatial layers, e.g. `L3T1`, `L3T3`, `L3T3_KEY`. */
const THREE_SPATIAL_LAYERS = /\bL3T[1-9](?:h|_KEY|_KEY_SHIFT)?\b/;

const SAFE_DEFAULT: PublishDecision = {
  mode: 'vp8-simulcast',
  codec: 'vp8',
  rationale: 'video send capabilities unavailable; safe default',
};

/**
 * Publish tier: how large the camera is captured and published for the WHOLE media
 * session. Resolved once per join from the machine's hardware signals plus the user's
 * optional override, and shared by the raw capture, the canvas processor's published
 * size and every re-acquisition (toggles, media handovers) - a raw<->canvas effects
 * swap therefore never changes the published dimensions (the dimension-changing
 * replaceTrack desyncs the SFU's layer bookkeeping and freezes subscribers; see
 * backgroundProcessor.ts's stability invariant).
 */
export type PublishTier = 'low' | 'medium' | 'high';

/** The stored user override for the publish tier ('auto' = hardware heuristic). */
export type CameraQualitySetting = 'auto' | PublishTier;

/**
 * Per-tier publish (and capture) caps, even-friendly dimensions. 'low' is the
 * conservative exception for machines without a usable hardware encoder; 'medium'
 * targets 720p and 'high' 1080p.
 */
export const PUBLISH_TIER_CAPS: Readonly<Record<PublishTier, { width: number; height: number }>> = {
  low: { width: 640, height: 360 },
  medium: { width: 1280, height: 720 },
  high: { width: 1920, height: 1080 },
};

/** Per-tier layer tables: the primary preset (capture size + full bitrate budget). */
const TIER_PRIMARY: Readonly<Record<PublishTier, VideoPreset>> = {
  low: new VideoPreset({ width: 640, height: 360, maxBitrate: 500_000, maxFramerate: 30 }),
  medium: new VideoPreset({ width: 1280, height: 720, maxBitrate: 2_300_000, maxFramerate: 30 }),
  high: new VideoPreset({ width: 1920, height: 1080, maxBitrate: 4_000_000, maxFramerate: 30 }),
};

/** The two simulcast layers under each tier's primary (order: low, medium). */
const TIER_SIMULCAST_LAYERS: Readonly<Record<PublishTier, readonly VideoPreset[]>> = {
  low: [new VideoPreset({ width: 320, height: 180, maxBitrate: 150_000, maxFramerate: 20 })],
  medium: [
    new VideoPreset({ width: 320, height: 180, maxBitrate: 150_000, maxFramerate: 20 }),
    new VideoPreset({ width: 640, height: 360, maxBitrate: 500_000, maxFramerate: 20 }),
  ],
  high: [
    new VideoPreset({ width: 640, height: 360, maxBitrate: 500_000, maxFramerate: 20 }),
    new VideoPreset({ width: 1280, height: 720, maxBitrate: 2_300_000, maxFramerate: 30 }),
  ],
};

/** The hardware signals the auto tier heuristic reads. */
export interface HardwareSignal {
  /** `navigator.userAgentData.platform` / `navigator.platform` / UA, lowercased or ''. */
  readonly platform: string;
  /** `navigator.hardwareConcurrency`; null when the API is missing. */
  readonly cores: number | null;
  /** `navigator.deviceMemory` (Chromium, GB); null when the API is missing. */
  readonly memoryGb: number | null;
}

/**
 * Reads the browser's hardware signals. Returns empty/nullable values where the APIs
 * are missing (jsdom, exotic embedders) - the resolver treats unknown as capable-ish
 * and lands on 'medium', keeping 'low' the explicit exception.
 */
export function detectHardwareSignal(): HardwareSignal {
  const nav = navigator as {
    platform?: unknown;
    hardwareConcurrency?: unknown;
    deviceMemory?: unknown;
    userAgent?: unknown;
    userAgentData?: { platform?: unknown };
  };
  const platformField =
    typeof nav.userAgentData?.platform === 'string'
      ? nav.userAgentData.platform
      : typeof nav.platform === 'string'
        ? nav.platform
        : typeof nav.userAgent === 'string'
          ? nav.userAgent
          : '';
  const cores = typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : null;
  const memoryGb = typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null;
  return { platform: platformField.toLowerCase(), cores, memoryGb };
}

/**
 * Resolves the publish tier for the session. Pure. The user override wins; 'auto'
 * applies the platform-aware heuristic:
 *  - macOS → 'high' (1080p): VideoToolbox hardware encode is always there, and the
 *    mission's problem case is exactly a capable Mac being treated as weak.
 *  - Windows → cores-based: 8+ cores 'high', fewer 'medium' - Media Foundation /
 *    D3D11 hardware encoders ship on essentially every modern box.
 *  - Linux / unknown → 'medium' (720p): Chromium's H264 encode there is often
 *    software (OpenH264; VAAPI is opt-in and uneven), and a software 1080p encode
 *    costs more than it delivers - measured on the reference host: with a
 *    subscriber active, the 3-layer software 1080p/720p/360p encode starved the
 *    effects engine start that a 720p publish leaves headroom for.
 *  - 'low' stays the conservative exception: so few cores (<= 2) or so little
 *    memory (<= 2 GB) that even a hardware encoder starves - approximating the
 *    software-encode fallback detection the platform APIs do not expose.
 * The user override (devicePrefs camQuality) is the manual escape hatch in every
 * direction (a Linux user with a known VAAPI box can pin 'high').
 */
export function resolvePublishTier(
  hw: HardwareSignal,
  override: CameraQualitySetting,
): PublishTier {
  if (override !== 'auto') {
    return override;
  }
  if (hw.platform.includes('mac')) {
    return 'high';
  }
  const memoryTooSmall = hw.memoryGb !== null && hw.memoryGb <= 2;
  const coresTooFew = hw.cores !== null && hw.cores <= 2;
  if (memoryTooSmall || coresTooFew) {
    return 'low';
  }
  if (hw.platform.includes('windows')) {
    return hw.cores !== null && hw.cores >= 8 ? 'high' : 'medium';
  }
  // Linux and unknown platforms: 720p by default (the conservative 1080p bar is
  // hardware-encode certainty, which only macOS carries today).
  return 'medium';
}

/**
 * The session tier with the effects-path rule applied. Pure.
 *
 * The effects pipeline composites at most 720p (BACKGROUND_QUALITY_PARAMS.high in
 * backgroundProcessor.ts) - a 'high' (1080p) session that STARTS with effects then encodes
 * an upscaled 720p composite into a 1080p canvas at the 1080p bitrate: the full encode cost
 * for zero extra detail. When the tier came from the AUTO heuristic (the user did not pin
 * camQuality) and the session starts with an active treatment, the tier therefore lands on
 * 'medium': capture, processing canvas and publish canvas all agree at 720p, the
 * raw<->canvas swap stays dimension-stable (backgroundProcessor.ts's invariant) and the
 * encode budget is real. A user-pinned tier wins in every direction - camQuality 'high'
 * keeps the 1080p effects session on purpose.
 *
 * Session-start only by construction: callers resolve once per join (and the prejoin preview
 * derives the same value from its live effect switches), so an in-call effects toggle never
 * changes published dimensions (the stall lesson).
 */
export function resolveSessionTier(
  hw: HardwareSignal,
  override: CameraQualitySetting,
  effectsOn: boolean,
): PublishTier {
  const tier = resolvePublishTier(hw, override);
  if (!effectsOn || override !== 'auto' || tier !== 'high') {
    return tier;
  }
  return 'medium';
}

/**
 * Reads the browser's video send capabilities. Returns null where the API is missing
 * (jsdom, insecure context) so the caller falls back to the safe default.
 */
export function detectVideoSendCapabilities(): RTCRtpCapabilities | null {
  if (typeof RTCRtpSender === 'undefined' || typeof RTCRtpSender.getCapabilities !== 'function') {
    return null;
  }
  return RTCRtpSender.getCapabilities('video');
}

/**
 * Picks the publish path from video send capabilities. Pure: every input shape
 * (H264-capable, VP9 SVC-capable, VP9 without SVC modes, VP8-only, empty/absent)
 * yields a typed decision with a rationale. H264 wins whenever the browser offers it:
 * it is the only broadly hardware-encoded family (VideoToolbox / MFT / VAAPI), while
 * the SVC and VP8 fallbacks software-encode in Chromium.
 */
export function decidePublishPath(
  capabilities: RTCRtpCapabilities | null | undefined,
): PublishDecision {
  const codecs = capabilities?.codecs;
  if (codecs === undefined || codecs.length === 0) {
    return SAFE_DEFAULT;
  }
  if (codecs.some((codec) => codec.mimeType === 'video/H264')) {
    return {
      mode: 'h264-simulcast',
      codec: 'h264',
      rationale: 'H264 offered (hardware encode: VideoToolbox / MFT / VAAPI)',
    };
  }
  const vp9 = codecs.find((codec) => codec.mimeType === 'video/VP9');
  if (vp9 !== undefined) {
    const profile = vp9Profile(vp9);
    const modes = scalabilityModesOf(vp9);
    if (profile === 0 && modes !== null && THREE_SPATIAL_LAYERS.test(modes)) {
      return {
        mode: 'vp9-svc',
        codec: 'vp9',
        scalabilityMode: SVC_SCALABILITY_MODE,
        rationale: `VP9 profile 0 with 3-spatial-layer modes (${modes}); H264 absent`,
      };
    }
    return simulcastDecision(codecs, vp9FallbackReason(profile, modes));
  }
  if (codecs.some((codec) => codec.mimeType === 'video/VP8')) {
    return { mode: 'vp8-simulcast', codec: 'vp8', rationale: 'H264 and VP9 not offered' };
  }
  return { ...SAFE_DEFAULT, rationale: `${SAFE_DEFAULT.rationale}; only unrecognised codecs` };
}

/** Publish options handed to setCameraEnabled for the decided path, sized by the tier. */
export function cameraPublishOptions(
  decision: PublishDecision,
  tier: PublishTier,
): TrackPublishOptions {
  // The source must ride the publish options explicitly: a PROCESSED camera (the background
  // processor's canvas track) is published through publishTrack, and without `source` the SDK
  // wraps the canvas MediaStreamTrack as Track.Source.Unknown - the publication then never
  // answers getTrackPublication(Source.Camera), so setCameraEnabled(false) is a silent no-op
  // (the camera toggle cannot mute the feed) and isCameraEnabled reads false while streaming.
  // For the raw getUserMedia path the SDK already assigns Camera; stating it is idempotent.
  const source: TrackPublishOptions = { source: Track.Source.Camera };
  if (decision.mode === 'vp9-svc') {
    return {
      ...source,
      videoCodec: 'vp9',
      scalabilityMode: decision.scalabilityMode ?? SVC_SCALABILITY_MODE,
      // One SVC stream carries the layers; rid simulcast must stay off for L3Tx modes.
      simulcast: false,
      // Full-stream bitrate (all spatial layers) at the tier's primary budget; the SDK
      // derives the SVC encodings.
      videoEncoding: TIER_PRIMARY[tier].encoding,
      // backupCodec stays at the SDK default (true): a VP8 track is published
      // automatically for subscribers that cannot decode VP9 SVC.
    };
  }
  return {
    ...source,
    videoCodec: decision.codec,
    simulcast: true,
    // Primary (high) layer at the tier's resolution; the two lower layers come from
    // videoSimulcastLayers.
    videoEncoding: TIER_PRIMARY[tier].encoding,
    videoSimulcastLayers: [...TIER_SIMULCAST_LAYERS[tier]],
  };
}

/**
 * Capture constraints for the camera: the tier's resolution cap at 30 fps. `deviceId`
 * may be null on re-acquisitions that do not know the session's device (the SDK then
 * opens its default) - the resolution cap still applies, keeping the published
 * dimensions stable across camera off/on toggles.
 */
export function cameraCaptureOptions(
  deviceId: string | null,
  tier: PublishTier,
): VideoCaptureOptions {
  return {
    ...(deviceId !== null ? { deviceId } : {}),
    frameRate: 30,
    resolution: { ...PUBLISH_TIER_CAPS[tier], frameRate: 30 },
  };
}

// The microphone capture options (device id + the effect switches) live in micEffects.ts:
// microphoneCaptureOptions(deviceId, settings) is the single mapping into AudioCaptureOptions.

/** Publish options for the microphone: speech bitrate with DTX and RED. */
export function microphonePublishOptions(): TrackPublishOptions {
  return {
    audioPreset: AudioPresets.speech,
    dtx: true,
    red: true,
  };
}

/**
 * The announced VP9 scalability modes when the browser can actually encode the
 * single-stream SVC shape (VP9 present, camera profile 0, three spatial layers),
 * or null when it cannot. Shared by the camera and screen-share publish decisions.
 */
export function vp9SvcModes(codecs: readonly RTCRtpCodec[]): string | null {
  const vp9 = codecs.find((codec) => codec.mimeType === 'video/VP9');
  if (vp9 === undefined || vp9Profile(vp9) !== 0) {
    return null;
  }
  const modes = scalabilityModesOf(vp9);
  if (modes === null || !THREE_SPATIAL_LAYERS.test(modes)) {
    return null;
  }
  return modes;
}

/** VP9 profile from sdpFmtpLine; an absent/empty fmtp line means the default profile 0. */
function vp9Profile(codec: RTCRtpCodec): number {
  const fmtp = codec.sdpFmtpLine;
  if (fmtp === undefined || fmtp.length === 0) {
    return 0;
  }
  const match = fmtp.match(/(?:^|;)\s*profile-id=(\d+)/);
  return match === null ? 0 : Number(match[1]);
}

/**
 * Reads the non-standard `scalabilityModes` field Chromium puts on each codec
 * capability (space-separated list, e.g. "L1T2 L1T3 ... L3T3"). Not part of lib.dom's
 * RTCRtpCodec, hence the single documented structural cast to read it as unknown.
 */
function scalabilityModesOf(codec: RTCRtpCodec): string | null {
  const field: unknown = (codec as { scalabilityModes?: unknown }).scalabilityModes;
  return typeof field === 'string' ? field : null;
}

function simulcastDecision(codecs: readonly RTCRtpCodec[], reason: string): PublishDecision {
  if (codecs.some((codec) => codec.mimeType === 'video/VP8')) {
    return { mode: 'vp8-simulcast', codec: 'vp8', rationale: reason };
  }
  if (codecs.some((codec) => codec.mimeType === 'video/H264')) {
    return { mode: 'h264-simulcast', codec: 'h264', rationale: `${reason}; VP8 absent` };
  }
  return { ...SAFE_DEFAULT, rationale: `${reason}; no simulcast-capable codec recognised` };
}

function vp9FallbackReason(profile: number, modes: string | null): string {
  if (profile !== 0) {
    return `VP9 profile ${profile} is not the camera profile 0`;
  }
  if (modes === null) {
    return 'VP9 without announced scalability modes (SVC unsupported)';
  }
  return `VP9 scalability modes lack 3 spatial layers (${modes})`;
}

/** One publisher-side sender stat row for the dev/e2e bridge (numbers only). */
export interface CameraSenderStatSnapshot {
  readonly rid: string;
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly framesPerSecond: number;
  readonly targetBitrate: number;
  readonly bytesSent: number;
  readonly timestamp: number;
}

/**
 * Dev-only e2e seam: exposes the camera publication's LiveKit sender stats
 * (LocalVideoTrack.getSenderStats: per-layer frame dimensions, fps and target
 * bitrate) to Playwright page evaluation (`window.__vcCamStats()`). Stripped from
 * production builds by the import.meta.env.DEV gate, like the camera-fx bridge.
 */
export function exposeCamStatsForTests(
  read: () => Promise<readonly CameraSenderStatSnapshot[] | null>,
): void {
  if (import.meta.env.DEV !== true) {
    return;
  }
  window.__vcCamStats = (): Promise<readonly CameraSenderStatSnapshot[] | null> => read();
}

declare global {
  interface Window {
    /** Dev/e2e only: the camera publication's sender stats (null without a camera). */
    __vcCamStats?: () => Promise<readonly CameraSenderStatSnapshot[] | null>;
  }
}
